import React, { useEffect, useRef, useState } from 'react';
import './pdf-wrapper.css';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

export const PdfWrapper = ({ children, isDebug, pdfFormat, triggerPdfOutputCallback }) => {
  pdfFormat = pdfFormat?.toLowerCase();
  if (!pdfFormat) throw new Error('Please provide a PDF format');
  if (pdfFormat !== 'a4' && pdfFormat !== 'us-letter') {
    throw new Error('Invalid PDF format. Please use "A4" or "US-Letter"');
  }
  const [styleVariables, setStyleVariables] = useState(null);
  const anchorRef = useRef(null);
  const [zIndex] = useState(isDebug ? 100000 : -1000);
  const renderPromiseFinished = useRef(false);
  const renderPromise = useRef(null);
  const commitPdfRender = useRef(null);
  const viewportRef = useRef(null);

  const closePdf = async (downloadFile) => {
    if (!renderPromiseFinished.current) {
      commitPdfRender.reject();
      renderPromiseFinished.current = true;
      await triggerPdfOutputCallback?.(null);
    } else if (downloadFile) {
      const pages = viewportRef.current.querySelectorAll('.pdf-page');
      await triggerPdfOutputCallback?.(pages);
    } else {
      await triggerPdfOutputCallback?.(null);
    }
    setStyleVariables(null);
  };

  useEffect(() => {
    renderPromise.current = new Promise((resolve, reject) => {
      renderPromiseFinished.current = false;
      commitPdfRender.current = {
        resolve,
        reject,
      };
    });

    const { x, y } = anchorRef.current.getBoundingClientRect();
    // reset the wrapper to page coord (0, 0)
    const data = {
      '--pdf-left': x * -1 + 'px',
      '--pdf-top': y * -1 + 'px',
      '--pdf-z-index': zIndex,
    };
    setStyleVariables(data);
  }, []);

  useEffect(() => {
    if (styleVariables !== null && !renderPromiseFinished.current) {
      commitPdfRender.current.resolve();
      renderPromiseFinished.current = true;
      if (!isDebug) {
        closePdf(true);
      }
    }
  }, [styleVariables]);

  return (
    <div
      className="pdf-anchor"
      ref={anchorRef}
      style={{ '--overflow-visibility': isDebug ? 'visible' : 'hidden', zIndex: zIndex }}
    >
      {styleVariables && (
        <div style={styleVariables} className={'pdf-wrapper ' + pdfFormat}>
          <div ref={viewportRef} className="pdf-wrapper-viewport">
            {children}
          </div>
          <div className="pdf-wrapper-spacer"></div>
        </div>
      )}
      {styleVariables && (
        <div style={styleVariables} className="pdf-close" onClick={() => closePdf(false)}>
          X
        </div>
      )}
      {styleVariables && (
        <div
          id="pdf-download"
          data-tooltip-id="pdf-download"
          data-tooltip-content="Trigger PDF Action"
          style={styleVariables}
          className="pdf-download"
          onClick={() => closePdf(true)}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.25 8.5V12C13.25 12.3315 13.1183 12.6495 12.8839 12.8839C12.6495 13.1183 12.3315 13.25 12 13.25H2C1.66848 13.25 1.35054 13.1183 1.11612 12.8839C0.881696 12.6495 0.75 12.3315 0.75 12V8.5C0.75 8.30109 0.829018 8.11032 0.96967 7.96967C1.11032 7.82902 1.30109 7.75 1.5 7.75C1.69891 7.75 1.88968 7.82902 2.03033 7.96967C2.17098 8.11032 2.25 8.30109 2.25 8.5V11.75H11.75V8.5C11.75 8.30109 11.829 8.11032 11.9697 7.96967C12.1103 7.82902 12.3011 7.75 12.5 7.75C12.6989 7.75 12.8897 7.82902 13.0303 7.96967C13.171 8.11032 13.25 8.30109 13.25 8.5ZM6.46938 9.03063C6.53905 9.10054 6.62185 9.15602 6.71301 9.19388C6.80417 9.23173 6.90191 9.25122 7.00062 9.25122C7.09934 9.25122 7.19707 9.23173 7.28824 9.19388C7.3794 9.15602 7.4622 9.10054 7.53187 9.03063L10.0319 6.53062C10.1728 6.38973 10.2519 6.19863 10.2519 5.99937C10.2519 5.80012 10.1728 5.60902 10.0319 5.46812C9.89098 5.32723 9.69988 5.24807 9.50062 5.24807C9.30137 5.24807 9.11027 5.32723 8.96937 5.46812L7.75 6.6875V1.5C7.75 1.30109 7.67098 1.11032 7.53033 0.96967C7.38968 0.829018 7.19891 0.75 7 0.75C6.80109 0.75 6.61032 0.829018 6.46967 0.96967C6.32902 1.11032 6.25 1.30109 6.25 1.5V6.6875L5.03062 5.46938C4.88973 5.32848 4.69863 5.24932 4.49937 5.24932C4.30012 5.24932 4.10902 5.32848 3.96812 5.46938C3.82723 5.61027 3.74807 5.80137 3.74807 6.00063C3.74807 6.19988 3.82723 6.39098 3.96812 6.53188L6.46938 9.03063Z" />
          </svg>
          <Tooltip id="pdf-download" place="top" effect="solid" delayShow={2500} delayHide={200}>
            Download PDF
          </Tooltip>
        </div>
      )}
    </div>
  );
};
